/**
 * Settings Validation Utilities
 *
 * Provides validation functions for common settings values like
 * ISO 8601 durations, numeric ranges, etc.
 *
 * Following LogicAppsUX validation patterns from:
 * /libs/designer/src/lib/ui/settings/validation/validation.ts
 */

import type { NodeSettings, RetryPolicy, ConcurrencySettings, PagingSettings, UploadChunk } from '@/types/nodeSettings';
import { ISO_8601_DURATION_PATTERN, parseIsoDurationToSeconds } from '@/utils/dateTime';

/**
 * Validate an ISO 8601 duration string
 * @param value - The duration string to validate
 * @returns Object with isValid boolean and optional error message
 */
export function validateDuration(value: string | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  if (!value || value.trim() === '') {
    return { isValid: true }; // Empty is valid (optional)
  }

  const trimmed = value.trim().toUpperCase();

  // Must start with P
  if (!trimmed.startsWith('P')) {
    return {
      isValid: false,
      error: 'Duration must start with "P" (e.g., PT1H, PT30M, P1D)',
    };
  }

  // Check if it matches the pattern
  if (!ISO_8601_DURATION_PATTERN.test(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid duration format. Use ISO 8601 format (e.g., PT1H, PT30M, P1DT12H)',
    };
  }

  // Check that it's not just "P" or "PT" with no values
  if (trimmed === 'P' || trimmed === 'PT') {
    return {
      isValid: false,
      error: 'Duration must have at least one time component (e.g., PT1H)',
    };
  }

  return { isValid: true };
}

/**
 * Validate a numeric value is within a range
 * @param value - The value to validate
 * @param min - Minimum allowed value (inclusive)
 * @param max - Maximum allowed value (inclusive)
 * @param fieldName - Name of the field for error messages
 * @returns Object with isValid boolean and optional error message
 */
export function validateRange(
  value: number | undefined | null,
  min: number,
  max: number,
  fieldName: string = 'Value'
): { isValid: boolean; error?: string } {
  if (value === undefined || value === null) {
    return { isValid: true }; // Empty is valid (optional)
  }

  if (typeof value !== 'number' || isNaN(value)) {
    return {
      isValid: false,
      error: `${fieldName} must be a number`,
    };
  }

  if (value < min || value > max) {
    return {
      isValid: false,
      error: `${fieldName} must be between ${min} and ${max}`,
    };
  }

  return { isValid: true };
}

/**
 * Validate retry count (1-90 per LogicApps limits)
 */
export function validateRetryCount(value: number | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  return validateRange(value, 1, 90, 'Retry count');
}

/**
 * Validate concurrency runs for triggers (1-50)
 */
export function validateTriggerConcurrency(value: number | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  return validateRange(value, 1, 50, 'Concurrent runs');
}

/**
 * Validate concurrency runs for actions (1-100)
 */
export function validateActionConcurrency(value: number | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  return validateRange(value, 1, 100, 'Concurrent runs');
}

/**
 * Validate paging count (1-5000)
 */
export function validatePagingCount(value: number | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  return validateRange(value, 1, 5000, 'Page size');
}

/**
 * Validate chunk size (1MB - 1024MB)
 */
export function validateChunkSize(value: number | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  return validateRange(value, 1, 1024, 'Chunk size (MB)');
}

/**
 * Validate a condition expression is not empty
 */
export function validateConditionExpression(value: string | undefined | null): {
  isValid: boolean;
  error?: string;
} {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      error: 'Condition expression cannot be empty',
    };
  }
  return { isValid: true };
}

// =============================================================================
// CROSS-FIELD VALIDATION
// =============================================================================

/**
 * Parse ISO 8601 duration to total seconds for comparison.
 * Thin wrapper around `parseIsoDurationToSeconds` from `utils/dateTime`
 * — kept private so callers continue using the higher-level helpers
 * defined in this file (`validateRetryIntervalRange`, etc.).
 */
function parseDurationToSeconds(duration: string | undefined): number | null {
  return parseIsoDurationToSeconds(duration);
}

/**
 * Validate that minimumInterval <= maximumInterval for exponential retry
 */
export function validateRetryIntervalRange(
  minimumInterval: string | undefined,
  maximumInterval: string | undefined
): { isValid: boolean; error?: string } {
  if (!minimumInterval || !maximumInterval) {
    return { isValid: true }; // Only validate if both are provided
  }

  const minSeconds = parseDurationToSeconds(minimumInterval);
  const maxSeconds = parseDurationToSeconds(maximumInterval);

  if (minSeconds === null || maxSeconds === null) {
    return { isValid: true }; // Can't compare if parsing fails (format errors caught elsewhere)
  }

  if (minSeconds > maxSeconds) {
    return {
      isValid: false,
      error: 'Minimum interval cannot be greater than maximum interval',
    };
  }

  return { isValid: true };
}

// =============================================================================
// COMPREHENSIVE VALIDATION
// =============================================================================

/**
 * Validate all settings and return a map of errors
 * @param settings - The NodeSettings object to validate
 * @param isTrigger - Whether this is a trigger (affects concurrency limits)
 * @returns Record<string, string> - Map of settingKey → errorMessage
 */
export function validateAllSettings(
  settings: NodeSettings,
  isTrigger: boolean = false
): Record<string, string> {
  const errors: Record<string, string> = {};

  // ==========================================================================
  // TIMEOUT VALIDATION
  // ==========================================================================
  if (settings.timeout?.isSupported && settings.timeout.value) {
    const result = validateDuration(settings.timeout.value);
    if (!result.isValid && result.error) {
      errors['timeout'] = result.error;
    }
  }

  // ==========================================================================
  // RETRY POLICY VALIDATION
  // ==========================================================================
  if (settings.retryPolicy?.isSupported && settings.retryPolicy.value) {
    const policy: RetryPolicy = settings.retryPolicy.value;

    // Only validate count/interval for fixed and exponential types
    if (policy.type === 'fixed' || policy.type === 'exponential') {
      // Validate count
      if (policy.count !== undefined) {
        const countResult = validateRetryCount(policy.count);
        if (!countResult.isValid && countResult.error) {
          errors['retryPolicy.count'] = countResult.error;
        }
      }

      // Validate interval
      if (policy.interval) {
        const intervalResult = validateDuration(policy.interval);
        if (!intervalResult.isValid && intervalResult.error) {
          errors['retryPolicy.interval'] = intervalResult.error;
        }
      }

      // Exponential-specific validation
      if (policy.type === 'exponential') {
        // Validate minimum interval format
        if (policy.minimumInterval) {
          const minResult = validateDuration(policy.minimumInterval);
          if (!minResult.isValid && minResult.error) {
            errors['retryPolicy.minimumInterval'] = minResult.error;
          }
        }

        // Validate maximum interval format
        if (policy.maximumInterval) {
          const maxResult = validateDuration(policy.maximumInterval);
          if (!maxResult.isValid && maxResult.error) {
            errors['retryPolicy.maximumInterval'] = maxResult.error;
          }
        }

        // Cross-field validation: min <= max
        const rangeResult = validateRetryIntervalRange(
          policy.minimumInterval,
          policy.maximumInterval
        );
        if (!rangeResult.isValid && rangeResult.error) {
          errors['retryPolicy.intervalRange'] = rangeResult.error;
        }
      }
    }
  }

  // ==========================================================================
  // CONCURRENCY VALIDATION
  // ==========================================================================
  if (settings.concurrency?.isSupported && settings.concurrency.value) {
    const concurrency: ConcurrencySettings = settings.concurrency.value;

    if (concurrency.enabled && concurrency.runs !== undefined) {
      const concurrencyResult = isTrigger
        ? validateTriggerConcurrency(concurrency.runs)
        : validateActionConcurrency(concurrency.runs);

      if (!concurrencyResult.isValid && concurrencyResult.error) {
        errors['concurrency.runs'] = concurrencyResult.error;
      }
    }
  }

  // ==========================================================================
  // PAGING VALIDATION
  // ==========================================================================
  if (settings.paging?.isSupported && settings.paging.value) {
    const paging: PagingSettings = settings.paging.value;

    if (paging.enabled && paging.value !== undefined) {
      const pagingResult = validatePagingCount(paging.value);
      if (!pagingResult.isValid && pagingResult.error) {
        errors['paging.value'] = pagingResult.error;
      }
    }
  }

  // ==========================================================================
  // CHUNKING VALIDATION
  // ==========================================================================
  if (settings.uploadChunk?.isSupported && settings.uploadChunk.value) {
    const chunk: UploadChunk = settings.uploadChunk.value;

    if (chunk.transferMode === 'chunked' && chunk.uploadChunkSize !== undefined) {
      const chunkResult = validateChunkSize(chunk.uploadChunkSize);
      if (!chunkResult.isValid && chunkResult.error) {
        errors['uploadChunk.uploadChunkSize'] = chunkResult.error;
      }
    }
  }

  // ==========================================================================
  // DOWNLOAD CHUNK SIZE VALIDATION
  // ==========================================================================
  if (settings.downloadChunkSize?.isSupported && settings.downloadChunkSize.value !== undefined) {
    const chunkResult = validateRange(settings.downloadChunkSize.value, 1, 100, 'Download chunk size (MB)');
    if (!chunkResult.isValid && chunkResult.error) {
      errors['downloadChunkSize'] = chunkResult.error;
    }
  }

  // ==========================================================================
  // REQUEST OPTIONS (TIMEOUT) VALIDATION
  // ==========================================================================
  if (settings.requestOptions?.isSupported && settings.requestOptions.value?.timeout) {
    const result = validateDuration(settings.requestOptions.value.timeout);
    if (!result.isValid && result.error) {
      errors['requestOptions.timeout'] = result.error;
    }
  }

  // ==========================================================================
  // CONDITION EXPRESSIONS VALIDATION (triggers only)
  // ==========================================================================
  if (isTrigger && settings.conditionExpressions?.isSupported && settings.conditionExpressions.value) {
    const conditions = settings.conditionExpressions.value;
    conditions.forEach((condition, index) => {
      const result = validateConditionExpression(condition);
      if (!result.isValid && result.error) {
        errors[`conditionExpressions[${index}]`] = result.error;
      }
    });
  }

  // ==========================================================================
  // COUNT VALIDATION (loop count)
  // ==========================================================================
  if (settings.count?.isSupported && settings.count.value !== undefined) {
    const countValue = settings.count.value;
    // Count can be a number or an expression string
    if (typeof countValue === 'number') {
      const result = validateRange(countValue, 1, 5000, 'Loop count');
      if (!result.isValid && result.error) {
        errors['count'] = result.error;
      }
    }
    // If it's a string, it's likely an expression - no validation needed
  }

  return errors;
}
